import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { trackGainDbToPercent } from '@/shared/track-gain.ts'
import useTracksStore, {
  clipSelector,
  trackSelector,
} from '../../../../shared/store/tracks.store.tsx'
import useUiStore from '../../../../shared/store/ui.store.tsx'
import { recordChange } from '@/features/clips/domain/use-cases/record-change.ts'

type Side = 'left' | 'right'

export interface VolumeLayerProps {
  trackId: string
  clipId: string
  width: number
  sideBeingResized: Side | undefined
}

const VolumeLayer: React.FC<VolumeLayerProps> = ({
  trackId,
  clipId,
  width,
  sideBeingResized,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const height = 80

  const { pixelsPerSecond } = useUiStore()
  const { updateClip } = useTracksStore()
  const { volume } = useTracksStore(trackSelector(trackId))
  const clip = useTracksStore(clipSelector(trackId, clipId))

  const [fadeInX, setFadeInX] = useState<number>(
    clip?.fadeIn ? clip?.fadeIn * pixelsPerSecond : 0
  )
  const [fadeOutX, setFadeOutX] = useState<number>(
    clip?.fadeOut ? clip?.fadeOut * pixelsPerSecond : 0
  )

  const [activeDot, setActiveDot] = useState<Side>()
  const [dragOffset, setDragOffset] = useState<number>(0)

  const [isHovering, setIsHovering] = useState(false)

  const minDotsDistance = 0.5 * pixelsPerSecond
  const volumeY = height - (trackGainDbToPercent(volume) / 100) * height
  const dotDiameter = isHovering ? 8 : 5

  const restrictedFadeInX = useCallback(
    (newPos: number) =>
      Math.max(0, Math.min(newPos, width - fadeOutX - minDotsDistance)),
    [fadeOutX, width, minDotsDistance]
  )

  const restrictedFadeOutX = useCallback(
    (newPos: number) =>
      Math.max(0, Math.min(newPos, width - fadeInX - minDotsDistance)),
    [fadeInX, width, minDotsDistance]
  )

  useEffect(() => {
    if (sideBeingResized === 'left') {
      if (fadeOutX === 0) {
        setFadeInX(restrictedFadeInX(fadeInX))
        return
      }

      setFadeOutX(restrictedFadeOutX(fadeOutX))
    } else if (sideBeingResized === 'right') {
      if (fadeInX === 0) {
        setFadeOutX(restrictedFadeOutX(fadeOutX))
        return
      }

      setFadeInX(restrictedFadeInX(fadeInX))
    } else {
      // When zoom changes
      setFadeInX(clip?.fadeIn ? clip?.fadeIn * pixelsPerSecond : 0)
      setFadeOutX(clip?.fadeOut ? clip?.fadeOut * pixelsPerSecond : 0)
    }
  }, [width, pixelsPerSecond])

  const handleMouseDown = useCallback(
    (dot: Side, e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.preventDefault()
      e.stopPropagation()
      if (!containerRef.current) return
      const containerRect = containerRef.current.getBoundingClientRect()
      const currentDotX = dot === 'left' ? fadeInX : width - fadeOutX
      const offset = e.clientX - containerRect.left - currentDotX
      setActiveDot(dot)
      setDragOffset(offset)
    },
    [fadeInX, fadeOutX, width]
  )

  useEffect(() => {
    if (!activeDot || !containerRef.current) return

    const handleMouseMove = (e: MouseEvent) => {
      if (!activeDot || !containerRef.current) return
      const containerRect = containerRef.current.getBoundingClientRect()
      const newX = e.clientX - containerRect.left - dragOffset

      if (activeDot === 'left') {
        setFadeInX(restrictedFadeInX(newX))
      } else if (activeDot === 'right') {
        setFadeOutX(
          width - Math.max(fadeInX + minDotsDistance, Math.min(newX, width))
        )
      }
    }

    const handleMouseUp = () => {
      setActiveDot(undefined)
      if (!clip) return

      updateClip(clipId, {
        fadeIn: fadeInX / pixelsPerSecond,
        fadeOut: fadeOutX / pixelsPerSecond,
      })
      recordChange()
    }

    if (activeDot) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [activeDot, dragOffset, fadeInX, fadeOutX, pixelsPerSecond])

  const renderFadeDot = (side: Side, x: number) => (
    <div
      onMouseDown={(e) => handleMouseDown(side, e)}
      className="fade-dot absolute cursor-move rounded-full bg-white"
      style={{
        width: dotDiameter,
        height: dotDiameter,
        [side]: x,
        top: volumeY - dotDiameter / 2,
      }}
    />
  )

  return (
    <div
      className="volume-layer group absolute left-0 top-0 flex h-full w-full items-center justify-center overflow-hidden"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div ref={containerRef} className="relative" style={{ width, height }}>
        <svg style={{ width, height }} className="overflow-visible">
          {/* Fade In */}
          {fadeInX > 0 && (
            <path
              d={`M 0 ${height} C 0 ${height}, 0 ${volumeY}, ${fadeInX} ${volumeY}`}
              fill="none"
              strokeWidth="1"
            />
          )}
          <line
            x1={fadeInX}
            y1={volumeY}
            x2={width - fadeOutX}
            y2={volumeY}
            strokeWidth="1"
          />
          {/* Fade Out */}
          {fadeOutX > 0 && (
            <path
              d={`M ${width - fadeOutX} ${volumeY} C ${width - 1} ${volumeY}, ${width - 1} ${height}, ${width - 1} ${height}`}
              fill="none"
              strokeWidth="1"
            />
          )}
        </svg>
        {renderFadeDot('left', restrictedFadeInX(fadeInX))}
        {renderFadeDot('right', restrictedFadeOutX(fadeOutX))}
      </div>
    </div>
  )
}

export default memo(VolumeLayer)
